import { OrderTypeModel, StoreModifierGroupModel } from "../models";
import { getEmbedConfig } from "../embed/config";

type Timing = {
  start_time: string;
  end_time: string;
};

// HIGHLIGHT: Added util for minute/hour normalization across helpers
function addMinutesToTime(
  hour: number,
  minute: number,
  delta: number,
): [number, number] {
  let min = minute + delta;
  let hr = hour;
  while (min >= 60) {
    min -= 60;
    hr++;
  }
  while (min < 0) {
    min += 60;
    hr--;
  }
  return [hr, min];
}

/**
 * The active store id.
 *
 * Previously parsed straight out of /olo/{storeId}. It now comes from the embed
 * config, which reproduces exactly that parsing when the app runs standalone and
 * otherwise takes the id the host page supplied (script attribute, custom
 * element attribute, or domain lookup performed by the loader).
 *
 * Kept under the original name so the existing call sites are unchanged.
 */
export function getStoreIdFromUrl() {
  // HIGHLIGHT: Add check for window for SSR/test safety
  if (typeof window === "undefined") return "";

  return getEmbedConfig().storeId;
}

/**
 * The UTM tag.
 *
 * This used to read the whole of location.hash. That is unusable in embed mode,
 * where the hash carries the route (website1.com/online-ordering#/menu) — the
 * router path would be swallowed and stored as a UTM tag. It also used to
 * accept any hash value at all, which meant an unrelated deep link fragment
 * (e.g. #opensignup) would get persisted to localStorage as a UTM tag.
 *
 * So: ?utm= query parameter first (works in both modes), and the legacy hash
 * form only where it cannot collide with a route AND only when it matches the
 * single ad-source tag configured via VITE_APP_UTM_TAG — never an arbitrary
 * hash.
 */
export function getUtmFromUrl() {
  // HIGHLIGHT: Add check for window for SSR/test safety
  if (typeof window === "undefined") return "";

  const fromQuery = new URLSearchParams(window.location.search).get("utm");
  if (fromQuery) return fromQuery;

  if (getEmbedConfig().routerMode === "hash") return "";

  const fromHash = window.location.hash.replace("#", "");
  const configuredUtmTag = import.meta.env.VITE_APP_UTM_TAG;
  return fromHash && fromHash === configuredUtmTag ? fromHash : "";
}

export function getNameInitials(name: string | null) {
  if (!name) return "G";
  const words = name.trim().split(/\s+/);
  // HIGHLIGHT: ensure initials are alphabetical, not empty strings
  const initials = words.map((word) => word.charAt(0) || "").join("");
  return initials ? initials.toUpperCase() : "G";
}

// HIGHLIGHT: Optimize Date object allocations and time arithmetic
export const generateTimeSlots = (
  startDate: Date,
  endDate: Date,
  intervalMinutes: number = 15,
): Date[] => {
  const slots: Date[] = [];
  let current = startDate.getTime();
  const end = endDate.getTime();

  while (current <= end) {
    slots.push(new Date(current));
    // HIGHLIGHT: Use timestamp arithmetic (reducing Date churn)
    let next = current + intervalMinutes * 60000;

    // Snap to hour logic (optional: clarify if needed)
    const currentMinutes = new Date(next).getMinutes();
    const diffToHour = 60 - (currentMinutes % 60);
    if (diffToHour < intervalMinutes) {
      next = new Date(next).setMinutes(intervalMinutes, 0, 0);
      next = new Date(next).setHours(new Date(next).getHours() + 1);
    }
    current = next;
  }

  return slots;
};

export const roundToNext15 = (date: Date) => {
  const minutes = date.getMinutes();
  const remainder = minutes % 15;
  if (remainder !== 0) {
    date.setMinutes(minutes + (15 - remainder));
  }
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date;
};

// HIGHLIGHT: Use shared addMinutesToTime and timestamp logic for timing slot bounds
export const getTimeSlotsInTimestamp = (
  date: Date,
  timings: Timing[],
  advTime: string,
): Date[] => {
  let allSlots: Date[] = [];
  const advanceMinutes = Number(advTime);
  const now = new Date();

  timings.forEach((timing) => {
    let [startHour, startMinute] = timing.start_time.split(":").map(Number);
    let [endHour, endMinute] = timing.end_time.split(":").map(Number);

    // --- Advance Start Time ---
    [startHour, startMinute] = addMinutesToTime(
      startHour,
      startMinute,
      advanceMinutes,
    );

    let startDateTime = new Date(date);
    startDateTime.setHours(startHour, startMinute, 0, 0);

    // --- Delayed End Time ---
    // if (endMinute === 0) endMinute = 60;
    [endHour, endMinute] = addMinutesToTime(
      endHour,
      endMinute,
      -advanceMinutes,
    );

    let endDateTime = new Date(date);
    endDateTime.setHours(endHour, endMinute, 0, 0);

    // console.log({startHour, startMinute, startDateTime, endHour, endMinute, endDateTime});

    // Now + advance time → rounded to next 15 min
    const nowPlusAdv = new Date(now.getTime() + advanceMinutes * 60000);
    const firstSlot = roundToNext15(
      new Date(Math.max(startDateTime.getTime(), nowPlusAdv.getTime())),
    );

    if (firstSlot > startDateTime) {
      startDateTime = firstSlot;
    }

    // Generate slots
    const timeSlots = generateTimeSlots(startDateTime, endDateTime);
    allSlots = [...allSlots, ...timeSlots];
  });
  return allSlots;
};

// HIGHLIGHT: Use shared helper for time math, guarantee string output
export const getAdvanceStartTime = (
  advanceTime: string,
  startTime: string[],
): string[] => {
  let [hr, min] = startTime.map(Number);
  [hr, min] = addMinutesToTime(hr, min, parseInt(advanceTime));
  return [hr.toString(), min.toString()];
};

export const getDelayedEndTime = (
  advanceTime: string,
  endTime: string[],
): string[] => {
  let [hr, min] = endTime.map(Number);
  if (min === 0) min = 60;
  [hr, min] = addMinutesToTime(hr, min, -parseInt(advanceTime));
  return [hr.toString(), min.toString()];
};

export const capitalize = (str: string) => {
  // HIGHLIGHT: null/empty guard
  return str && str.length ? str.charAt(0).toUpperCase() + str.slice(1) : "";
};

export const getModifierGroupDescription = (
  modifierGroup: StoreModifierGroupModel,
) => {
  const { g_min_selection, g_max_selection } = modifierGroup;
  let message = "Select Any Items";

  if (g_min_selection == 1 && g_max_selection == 0)
    message = `Select minimum ${g_min_selection} Item`;
  else if (g_min_selection == 1 && g_max_selection == 1)
    message = `Select any ${g_min_selection} Item`;
  else if (g_min_selection == 1 && g_max_selection > 1)
    message = `Select minimum ${g_min_selection} Item (Max - ${g_max_selection})`;
  else if (g_min_selection == 0 && g_max_selection == 1)
    message = `Select any ${g_max_selection} Item`;
  else if (g_min_selection == 0 && g_max_selection > 1)
    message = `Select any Items (Max - ${g_max_selection})`;
  else if (g_min_selection > 1) message = `Select any ${g_min_selection} Items`;

  return message;
};

export const ucwords = (str: string): string => {
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

export function getOrderTypeTimeSlots(orderType: OrderTypeModel): Date[] {
  const today = new Date();
  const todayIndex = today.getDay() || 7;
  const timings = (orderType.timing as any)[todayIndex] || [];
  const slots = getTimeSlotsInTimestamp(
    today,
    timings,
    orderType.advance_time.toString(),
  );
  return slots.filter((slot) => slot > today);
}

// Whether any day within the order type's advance window (today included)
// still has at least one future slot available. Used to decide whether the
// "Schedule for later" option should be offered at all when today is closed.
export function hasAdvanceSlotsAvailable(orderType: OrderTypeModel): boolean {
  const advanceDays = Number(orderType.advance_day);
  const now = new Date();

  for (let i = 0; i <= advanceDays; i++) {
    const date = new Date();
    date.setDate(date.getDate() + i);
    date.setHours(0, 0, 0, 0);

    const dayIndex = date.getDay() || 7;
    const timings = (orderType.timing as any)[dayIndex];
    if (!timings) continue;

    const slots = getTimeSlotsInTimestamp(
      date,
      timings,
      orderType.advance_time.toString(),
    );
    if (slots.some((slot) => slot.getTime() > now.getTime())) return true;
  }

  return false;
}
